<script setup lang="ts">
    import { computed, onMounted } from 'vue';
    import { useRoute, useRouter } from 'vue-router';
    import { useSqlEditorStore } from '@/stores/sqlEditorStore.ts';
    import { SavedQueryHeaderInfo } from '@/shared/helpers/api';
    import { buildFolderStructure } from '@/shared/helpers/poorMansFolderStructure';
    import { getQueryReportLinkBySavedQuery } from '@/shared/helpers/routing';
    import SavedQueryTreeItem from '@/apps/sqlEditor/SavedQueryTreeItem.vue';
    import SqlResult from '@/apps/sqlResult/SqlResult.vue';

    type Props = {
        guid?: string | null;
    };

    const props = withDefaults(defineProps<Props>(), {
        guid: null
    });

    const route = useRoute();
    const router = useRouter();
    const store = useSqlEditorStore();

    function queryPath(query: SavedQueryHeaderInfo): string {
        if (!query.isPublic && query.isShared && query.userId !== store.userId) {
            return `Shared queries/${query.userName}/${query.name}`;
        }
        return `${query.isPublic ? 'Public queries' : 'My queries'}/${query.name}`;
    }

    const folders = computed(() => {
        if (store.savedQueriesAll) {
            return buildFolderStructure(store.savedQueriesAll, queryPath);
        }
        return [];
    });

    async function openReport(q: SavedQueryHeaderInfo): Promise<void> {
        await router.push(getQueryReportLinkBySavedQuery(q.guid, q.name));
    }

    // Direct /guid/:guid URLs may not include title in query; add it so route meta can use report name as page/tab title.
    async function setMissingTitleInRoute(): Promise<void> {
        if (!props.guid || route.query.title)
            return;

        const query = store.savedQueriesAll.find(x => x.guid === props.guid);
        if (query?.name) {
            await router.replace(getQueryReportLinkBySavedQuery(props.guid, query.name));
        }
    }

    onMounted(async () => {
        await store.init();
        await setMissingTitleInRoute();
    });
</script>

<template>
    <div class="columns">
        <div class="column is-3">
            <p class="block">
                Click a query below to open the report.
            </p>
            <div class="block">
                <ul v-if="folders.length > 0">
                    <saved-query-tree-item
                        v-for="(item, idx) in folders"
                        :key="idx"
                        :item="item"
                        :is-bold="true"
                        :user-id="store.userId"
                        :expand-levels="1"
                        :query-click-callback="openReport" />
                </ul>
                <span v-else>
                    There are no saved queries available.
                </span>
            </div>
        </div>
        <div class="column is-9">
            <sql-result
                v-if="props.guid"
                :guid="props.guid" />
        </div>
    </div>
</template>
